import React, { useMemo, Fragment } from 'react';
import { motion } from 'framer-motion';
/**
 * NumberLineLeaps
 * ─────────────────────────────────────────────────────────────────────────────
 * Annotated number line that visualizes the **rijgen** (sequential leaps)
 * strategy for mental arithmetic — chaining several jumps along a number line.
 *
 *   Example: 47 + 28 → 47 +20→ 67 +8→ 75
 *   Example: 298 + 47 → 298 +2→ 300 +45→ 345
 *   Example: 63 − 28 → 63 −3→ 60 −25→ 35
 *
 * Each leap is rendered as an arc above the line with its size as a badge.
 * Landing points (start, intermediates, end) are dotted on the line with
 * their value labels below.
 *
 * Used as **support visual** for REK-HANDIG-PLUS-MIN, REK-CONTEXT-PLUS-MIN
 * and similar "rijgen via tientallen / honderdtallen" subskills.
 */
export interface NumberLineLeapsProps {
  /** Starting value (the first operand). */
  start: number;
  /**
   * Sequential leaps along the line.
   *   - `size` is the signed delta (positive = jump right, negative = jump left).
   *   - `label` overrides the auto-generated badge text (e.g. "+ 2").
   */
  leaps: Array<{
    size: number;
    label?: string;
  }>;
  /** Optional caption shown above the line (e.g. "298 + 47 = ?"). */
  label?: string;
  /**
   * Hide the value of the final landing point so the visual does not spoil
   * the answer. Used when rendered alongside the question stem.
   */
  hideEndValue?: boolean;
  /** Rendering hint — stimulus is compact; support adds an explanation card. */
  mode?: 'stimulus' | 'support';
}
const dutchNumber = (n: number): string => new Intl.NumberFormat('nl-NL').format(n);
const formatSignedLabel = (size: number): string => {
  if (size > 0) return `+ ${dutchNumber(size)}`;
  if (size < 0) return `− ${dutchNumber(Math.abs(size))}`;
  return '0';
};
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
/** Map a value onto the [4,96]% axis, padded so endpoints don't sit on edges. */
function project(value: number, min: number, max: number): number {
  if (max === min) return 50;
  const t = (value - min) / (max - min);
  return 4 + t * 92;
}
// Colour rotation for leaps — keeps the visual lively without being chaotic.
const LEAP_COLORS = [{
  stroke: '#4f46e5',
  badge: 'bg-indigo-600'
}, {
  stroke: '#0d9488',
  badge: 'bg-teal-600'
}, {
  stroke: '#d97706',
  badge: 'bg-amber-500'
}, {
  stroke: '#db2777',
  badge: 'bg-pink-600'
} // pink
] as const;
export function NumberLineLeaps({
  start,
  leaps,
  label,
  hideEndValue = false,
  mode = 'support'
}: NumberLineLeapsProps) {
  const computed = useMemo(() => {
    if (!isFiniteNumber(start)) return null;
    if (!Array.isArray(leaps) || leaps.length === 0) return null;
    // Walk the leaps and accumulate the landing positions.
    const positions: number[] = [start];
    let cursor = start;
    for (const leap of leaps) {
      if (!leap || typeof leap.size !== 'number' || !Number.isFinite(leap.size)) return null;
      cursor = cursor + leap.size;
      positions.push(cursor);
    }
    const rawMin = Math.min(...positions);
    const rawMax = Math.max(...positions);
    const span = Math.max(rawMax - rawMin, 1);
    const pad = Math.max(Math.round(span * 0.1), 1);
    return {
      positions,
      min: rawMin - pad,
      max: rawMax + pad
    };
  }, [start, leaps]);
  if (!computed) {
    return <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200 text-center" data-id="element-12354">
        <p className="text-red-600 font-medium" data-id="element-12355">
          Ongeldige rijgen-getallenlijn configuratie
        </p>
      </div>;
  }
  const {
    positions,
    min,
    max
  } = computed;
  const end = positions[positions.length - 1];
  const isStimulus = mode === 'stimulus';
  const numberLineViz = <div className="relative h-44 mt-4" aria-label="Getallenlijn met opeenvolgende sprongen (rijgen)" data-id="element-12356">
      {/* ── Main line ──────────────────────────────────────────────────── */}
      <div className="absolute top-[70%] left-0 right-0 h-2 bg-gradient-to-r from-indigo-400 via-teal-500 to-amber-400 rounded-full shadow-md -translate-y-1/2" data-id="element-12357" />

      {/* ── Leap arcs ──────────────────────────────────────────────────── */}
      {leaps.map((leap, index) => {
      const fromValue = positions[index];
      const toValue = positions[index + 1];
      const fromPct = project(fromValue, min, max);
      const toPct = project(toValue, min, max);
      const left = Math.min(fromPct, toPct);
      const width = Math.abs(toPct - fromPct);
      const direction = toPct >= fromPct ? 'right' : 'left';
      const color = LEAP_COLORS[index % LEAP_COLORS.length];
      const badgeText = leap.label ?? formatSignedLabel(leap.size);
      return <motion.div key={index} initial={{
        opacity: 0,
        scaleX: 0
      }} animate={{
        opacity: 1,
        scaleX: 1
      }} transition={{
        duration: 0.45,
        ease: 'easeOut',
        delay: 0.15 + index * 0.25
      }} style={{
        left: `${left}%`,
        width: `${Math.max(width, 4)}%`,
        transformOrigin: direction === 'right' ? 'left' : 'right'
      }} className="absolute top-[28%]" data-id="element-12358">
            <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-8 overflow-visible" data-id="element-12359">
              <path d="M 2 28 Q 50 -8 98 28" fill="none" stroke={color.stroke} strokeWidth="2.5" strokeLinecap="round" data-id="element-12360" />
              {direction === 'right' ? <polygon points="98,28 92,24 92,32" fill={color.stroke} data-id="element-12361" /> : <polygon points="2,28 8,24 8,32" fill={color.stroke} data-id="element-12362" />}
            </svg>
            <div className={`absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full ${color.badge} text-white px-3 py-1 rounded-lg text-sm font-bold shadow-md whitespace-nowrap`} data-id="element-12363">
              {badgeText}
            </div>
          </motion.div>;
    })}

      {/* ── Landing markers on the line ────────────────────────────────── */}
      {positions.map((value, index) => {
      const isStart = index === 0;
      const isEnd = index === positions.length - 1;
      const pct = project(value, min, max);
      const display = isEnd && hideEndValue ? '?' : dutchNumber(value);
      const variant: 'start' | 'intermediate' | 'end' | 'unknown' = isStart ? 'start' : isEnd ? hideEndValue ? 'unknown' : 'end' : 'intermediate';
      // Stagger if there are many leaps to prevent overlap
      const stagger = leaps.length >= 3 && index % 2 !== 0;
      return <Marker key={index} pct={pct} value={display} variant={variant} delay={0.1 + index * 0.12} stagger={stagger} data-id="element-12364" />;
    })}
    </div>;
  if (isStimulus) {
    return <div className="bg-indigo-50 rounded-xl px-6 pt-4 pb-10 border border-indigo-200" data-id="element-12365">
        {label && <p className="text-sm font-semibold text-indigo-800 mb-2 text-center" data-id="element-12366">
            {label}
          </p>}
        {numberLineViz}
      </div>;
  }
  return <div className="bg-gradient-to-br from-indigo-50 to-teal-50 p-8 rounded-2xl border-2 border-indigo-200 shadow-sm" data-id="element-12367">
      {label && <div className="mb-2 text-center" data-id="element-12368">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-12369">
            <div className="w-2.5 h-2.5 rounded-full bg-teal-600 animate-pulse" data-id="element-12370" />
            <span className="text-base font-bold text-indigo-900" data-id="element-12371">{label}</span>
          </div>
        </div>}

      {numberLineViz}

      <div className="mt-6 bg-white/80 rounded-xl p-5 border border-indigo-100 shadow-sm" data-id="element-12372">
        <div className="flex items-start gap-3" data-id="element-12373">
          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0" data-id="element-12374">
            <span className="text-teal-600 text-lg" data-id="element-12375">🪜</span>
          </div>
          <div className="flex-1" data-id="element-12376">
            <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-12377">
              Rijgen: spring in stappen langs de getallenlijn
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12378">
              Begin bij <strong data-id="element-12379">{dutchNumber(start)}</strong>
              {leaps.map((leap, i) => {
              const isLast = i === leaps.length - 1;
              const landing = positions[i + 1];
              return <Fragment key={i} data-id="element-12380">
                    , spring{' '}
                    <strong className="text-indigo-700" style={{
                  color: LEAP_COLORS[i % LEAP_COLORS.length].stroke
                }} data-id="element-12381">
                      {leap.label ?? formatSignedLabel(leap.size)}
                    </strong>
                    {!isLast || !hideEndValue ? <>
                        {' '}
                        naar <strong data-id="element-12382">{dutchNumber(landing)}</strong>
                      </> : null}
                  </Fragment>;
            })}
              {!hideEndValue && <>
                  {' '}
                  → eindigt op <strong data-id="element-12383">{dutchNumber(end)}</strong>
                </>}
              .
            </p>
          </div>
        </div>
      </div>
    </div>;
}
// ─── Marker subcomponent ────────────────────────────────────────────────────
interface MarkerProps {
  pct: number;
  value: string;
  variant: 'start' | 'intermediate' | 'end' | 'unknown';
  delay?: number;
  stagger?: boolean;
}
const VARIANT_STYLES: Record<MarkerProps['variant'], {
  dot: string;
  label: string;
}> = {
  start: {
    dot: 'bg-indigo-600 ring-indigo-200',
    label: 'text-indigo-900 border-indigo-200'
  },
  intermediate: {
    dot: 'bg-teal-500 ring-teal-200',
    label: 'text-teal-900 border-teal-200'
  },
  end: {
    dot: 'bg-emerald-600 ring-emerald-200',
    label: 'text-emerald-900 border-emerald-200'
  },
  unknown: {
    dot: 'bg-amber-500 ring-amber-200',
    label: 'text-amber-900 border-amber-200'
  }
};
function Marker({
  pct,
  value,
  variant,
  delay = 0.1,
  stagger = false
}: MarkerProps) {
  const styles = VARIANT_STYLES[variant];
  const isUnknown = variant === 'unknown';
  return <motion.div initial={{
    scale: 0,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} transition={{
    type: 'spring',
    stiffness: 400,
    damping: 24,
    delay
  }} className="absolute top-[70%] -translate-y-1/2 -translate-x-1/2 z-10" style={{
    left: `${pct}%`
  }} data-id="element-12384">
      <div className={`w-5 h-5 rounded-full border-2 border-white shadow-lg ring-4 ${styles.dot} ${isUnknown ? 'animate-pulse' : ''} mx-auto`} data-id="element-12385" />
      <div className={`absolute ${stagger ? 'top-12' : 'top-7'} left-1/2 -translate-x-1/2 text-xs font-bold bg-white/90 px-2 py-0.5 rounded-lg border shadow-sm whitespace-nowrap ${styles.label}`} data-id="element-12386">
        {value}
      </div>
      {stagger && <div className="absolute top-6 left-1/2 -translate-x-1/2 w-px h-5 bg-slate-300" data-id="element-12387" />}
    </motion.div>;
}